import "@/components/cinematic/cinematic.css";
import SEO from "@/components/SEO";

import CinematicHeader from "@/components/cinematic/CinematicHeader";
import CinematicHero from "@/components/cinematic/CinematicHero";
import CinematicChapter from "@/components/cinematic/CinematicChapter";
import CinematicDissolve from "@/components/cinematic/CinematicDissolve";
import CinematicPricing from "@/components/cinematic/CinematicPricing";
import CinematicLead from "@/components/cinematic/CinematicLead";
import CinematicFooter from "@/components/cinematic/CinematicFooter";
import ScrollProgress from "@/components/cinematic/ScrollProgress";
import StickyContactBar from "@/components/premium/StickyContactBar";

/** Higgsfield-style cinematic homepage. */
const CinematicHomePage = () => (
  <div className="min-h-screen cinematic-root" data-testid="cinematic-home">
    <SEO
      title="2161 Todd Lane · Beach Escape · Indialantic, FL 32903 | Luxury Furnished Rental"
      description="2161 Todd Ln, Indialantic, FL 32903 — Beach Escape. A four-bedroom furnished beach house, three-minute walk to the Atlantic. Ninety-day minimum stays from $4,250/month. Book direct."
      path="/"
    />
    <ScrollProgress />
    <CinematicHeader />

    <main>
      {/* HERO */}
      <CinematicHero
        eyebrow="2161 Todd Ln · Indialantic · Florida · 32903"
        headline="A three-minute walk"
        italic="to the Atlantic."
        body="A four-bedroom furnished beach house at 2161 Todd Lane, Indialantic, FL — built for the guests who stay a season, not a weekend."
        image="/photos/DSC09965.webp"
      />

      {/* CHAPTER 01 — THE ARRIVAL */}
      <CinematicChapter
        chapterNum="01"
        chapterLabel="The Arrival"
        headline="Todd Lane opens"
        italic="onto the ocean."
        body="Cross A1A at Fifth Avenue and you're on Indialantic Beach. No shuttles. No elevators. No beach-club dues. Just wide, quiet Atlantic sand."
        image="https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/75r1b3hg_IMG_6451.jpeg"
        align="left"
        stats={[
          { value: "3 min", label: "Walk" },
          { value: "850 ft", label: "To sand" },
          { value: "24 / 7", label: "Access" },
        ]}
      />

      <CinematicDissolve
        num="02"
        label="Inside"
        from="/photos/DSC00019.webp"
        to="/photos/DSC09967.webp"
      />

      {/* CHAPTER 02 — INSIDE */}
      <CinematicChapter
        chapterNum="02"
        chapterLabel="Inside"
        headline="Four bedrooms."
        italic="Room for the whole crew."
        body="Sleeps ten across four real bedrooms. Full kitchen. Laundry. Spa bathrooms. A living room that opens straight through to the covered patio."
        image="/photos/DSC09967.webp"
        align="right"
      />

      <CinematicDissolve
        num="03"
        label="The Reveal"
        from="/photos/DSC00088.webp"
        to="/photos/DSC00037.webp"
      />

      {/* CHAPTER 03 — THE REVEAL (game room) */}
      <CinematicChapter
        chapterNum="03"
        chapterLabel="The Reveal"
        headline="A real arcade —"
        italic="not a token machine."
        body="Full-size arcade cabinets. A Legends pinball machine. A giant tic-tac-toe wall. Kids won't need screens. Adults will forget they ever did."
        image="/photos/DSC00037.webp"
        align="left"
      />

      <CinematicDissolve
        num="04"
        label="Outside"
        from="/photos/DSC00042.webp"
        to="https://customer-assets-rejwkqb3.emergentagent.net/job_beach-escape-fl/artifacts/29m8az70_Photo%20Jun%2023%202026%2C%204%2026%2013%20PM.webp"
      />

      {/* CHAPTER 04 — OUTSIDE */}
      <CinematicChapter
        chapterNum="04"
        chapterLabel="Outside"
        headline="A tiki bar,"
        italic="a table nobody leaves."
        body="Bamboo tiki bar with stools. Backyard mini-golf. Kettler ping-pong. A screened patio, a covered lounge, a fenced yard the dog will thank you for."
        image="https://customer-assets-rejwkqb3.emergentagent.net/job_beach-escape-fl/artifacts/29m8az70_Photo%20Jun%2023%202026%2C%204%2026%2013%20PM.webp"
        align="right"
      />

      <CinematicDissolve
        num="05"
        label="The Ocean"
        from="https://customer-assets-rejwkqb3.emergentagent.net/job_beach-escape-fl/artifacts/prnl6l0o_Photo%20Jun%2023%202026%2C%204%2029%2021%20PM.webp"
        to="/photos/DSC00019.webp"
      />

      {/* CHAPTER 05 — THE OCEAN (scroll-scrubbed beach video) */}
      <CinematicChapter
        chapterNum="05"
        chapterLabel="The Ocean"
        headline="Wide, quiet, and warm"
        italic="every month of the year."
        body="Water averages 71°F in January, 84°F in August. From May to October the beach is a nesting site for loggerhead sea turtles."
        video="/video/beach.mp4"
        poster="/video/beach-poster.webp"
        scrubVideo
        align="left"
        stats={[
          { value: "71°F", label: "January" },
          { value: "84°F", label: "August" },
          { value: "5–10", label: "Turtles" },
        ]}
      />

      {/* CHAPTER 06 — WHY IT'S DIFFERENT (front exterior — location-focused) */}
      <CinematicChapter
        chapterNum="06"
        chapterLabel="Why It's Different"
        headline="Fifteen to L3Harris."
        italic="Ten to the hospital."
        body="At the center of Florida's Space Coast — minutes from Melbourne, Palm Bay, Satellite Beach, and Patrick Space Force Base. Direct corporate invoicing available."
        image="https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/75r1b3hg_IMG_6451.jpeg"
        align="right"
        stats={[
          { value: "15 min", label: "L3Harris" },
          { value: "10 min", label: "Hospital" },
          { value: "20 min", label: "Airport" },
        ]}
      />

      {/* CHAPTER 07 — THE DETAILS */}
      <CinematicPricing />

      <CinematicLead />
    </main>

    <CinematicFooter />
    <StickyContactBar />
  </div>
);

export default CinematicHomePage;
